/**
 * What the browser tells the page about the phone keyboard, on screen for a recording: turned on for this device with
 * `?kb=debug`, off with `?kb=off`. Samsung Internet in a Galaxy recording (2026-10-31): the visible part shrinks once,
 * to the keyboard's final height, ~76 ms after the tap, while the keyboard rises from ~110 to ~260 ms; nothing is
 * reported in between, so the messenger cannot ride on the keyboard the way a native app does. An input the keyboard
 * would cover makes the browser push the page up itself (visualViewport.offsetTop 0 → 327 over ~200 ms from ~110 ms),
 * and window.scrollTo(0, 0) does not bring it back.
 */

export type KeyboardMode = 'debug';

const MODE_KEY = 'sd.keyboard-mode';
/** Live numbers are redrawn every frame this long after the last event. */
const LIVE_MS = 2500;
const LINES = 7;

/** The mode kept for this device, after taking a `?kb=` from the address (and dropping it from there). */
export function keyboardMode(): KeyboardMode | null {
  try {
    const url = new URL(window.location.href);
    const asked = url.searchParams.get('kb');
    if (asked !== null) {
      if (asked === 'debug') localStorage.setItem(MODE_KEY, asked);
      else localStorage.removeItem(MODE_KEY);
      url.searchParams.delete('kb');
      window.history.replaceState(window.history.state, '', url);
    }
    const mode = localStorage.getItem(MODE_KEY);
    return mode === 'debug' ? mode : null;
  } catch {
    return null;
  }
}

export interface KeyboardProbe {
  /** An event, timed from the last focus or blur (`start`). */
  log(what: string, start?: boolean): void;
  stop(): void;
}

export function keyboardProbe(mode: KeyboardMode): KeyboardProbe {
  const viewport = window.visualViewport!;
  const box = document.createElement('div');
  box.setAttribute('aria-hidden', 'true');
  box.style.cssText =
    'position:fixed;left:0;right:0;top:0;z-index:var(--z-toast);padding:calc(env(safe-area-inset-top,0px) + 4px) 8px 4px;' +
    'font:600 11px/1.35 ui-monospace,Menlo,monospace;color:#fff;background:rgba(0,0,0,.78);pointer-events:none;white-space:pre;transform-origin:0 0';
  document.body.append(box);
  const lines: string[] = [];
  let zero = performance.now();
  let last = 0;
  let frame = 0;
  const ms = () => Math.round(performance.now() - zero);
  const draw = () => {
    const lift = document.documentElement.style.getPropertyValue('--kb-lift') || '0px';
    const live = `${mode} +${ms()} H${window.innerHeight} vh${Math.round(viewport.height)} top${Math.round(viewport.offsetTop)} lift${lift}`;
    box.textContent = [live, ...lines].join('\n');
    // Kept on the visible part when the browser pushes the page up, which shows how far.
    box.style.transform = `translateY(${viewport.offsetTop}px)`;
  };
  const tick = () => {
    draw();
    frame = performance.now() - last < LIVE_MS ? requestAnimationFrame(tick) : 0;
  };
  const log = (what: string, start = false) => {
    if (start) zero = performance.now();
    lines.unshift(`+${ms()} ${what}`);
    lines.length = Math.min(lines.length, LINES);
    last = performance.now();
    draw();
    if (!frame) frame = requestAnimationFrame(tick);
  };
  log('ready', true);
  return {
    log,
    stop: () => {
      cancelAnimationFrame(frame);
      box.remove();
    },
  };
}
